import { useMemo, useState } from "react";
import { Button, Checkbox, Popover, PopoverSurface, Text, ToggleButton, makeStyles, tokens } from "@fluentui/react-components";
import { Delete16Regular, Dismiss16Regular } from "@fluentui/react-icons";
import { startOfWeek } from "date-fns";
import { WEEKDAYS, type ClientPreferenceInput, type Weekday } from "@gymplan/shared";
import type { AvailabilitySlot } from "../lib/api.ts";
import { WEEKDAY_LABEL, WEEKDAY_SHORT } from "../lib/format.ts";
import { WeekCalendar, type CalBlock } from "./WeekCalendar.tsx";

const useStyles = makeStyles({
  anyRow: { display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" },
  legend: { display: "flex", gap: "14px", fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  swatch: { display: "inline-block", width: "12px", height: "12px", borderRadius: "3px", verticalAlign: "-1px", marginRight: "4px" },
});

/**
 * Client preference editor on the week grid.
 *  - Specific windows: drag to create / move / resize, click to pick gyms or remove.
 *  - "Any time" days: toggle buttons above the grid (a general slot with no times).
 *  - Trainer availability is drawn underneath, read-only, so clients can see what's possible.
 */
export function PreferenceCalendar({
  value,
  onChange,
  gyms,
  availability = [],
  sessionLengthMinutes = 60,
}: {
  value: ClientPreferenceInput[];
  onChange: (v: ClientPreferenceInput[]) => void;
  gyms: { id: string; name: string; isPrimary: boolean }[];
  availability?: AvailabilitySlot[];
  sessionLengthMinutes?: number;
}) {
  const s = useStyles();
  const [selected, setSelected] = useState<{ idx: number; anchor: HTMLElement } | null>(null);
  const week = useMemo(() => startOfWeek(new Date(), { weekStartsOn: 1 }), []);
  const primary = gyms.find((g) => g.isPrimary);
  const gymName = (id: string | null) => gyms.find((g) => g.id === (id ?? primary?.id))?.name ?? "";

  const specificIdx = value.map((p, i) => (p.startTime ? i : -1)).filter((i) => i >= 0);
  const generalDays = new Set(value.filter((p) => !p.startTime).map((p) => p.weekday));

  const blocks = useMemo<CalBlock[]>(() => {
    const avail: CalBlock[] = availability.map((a, i) => ({
      id: `avail-${i}`,
      weekday: a.weekday as Weekday,
      startTime: a.startTime,
      endTime: a.endTime,
      title: gyms.length > 1 ? gymName(a.gymId) : "Trainer available",
      kind: "availability",
      readOnly: true,
    }));
    const prefs: CalBlock[] = specificIdx.map((i) => {
      const p = value[i]!;
      const names = p.gymIds.map((g) => gyms.find((x) => x.id === g)?.name).filter(Boolean);
      return { id: `pref-${i}`, weekday: p.weekday, startTime: p.startTime!, endTime: p.endTime!, title: names.length ? names.join(", ") : gyms.length > 1 ? "Any gym" : undefined, kind: "preference" };
    });
    return [...avail, ...prefs];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, availability, gyms]);

  const edit = (idx: number, patch: Partial<ClientPreferenceInput>) => onChange(value.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const remove = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
    setSelected(null);
  };
  const toggleGeneral = (wd: Weekday) =>
    generalDays.has(wd) ? onChange(value.filter((p) => !(p.weekday === wd && !p.startTime))) : onChange([...value, { weekday: wd, gymIds: [] }]);

  const sel = selected ? value[selected.idx] : undefined;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div className={s.anyRow}>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          Any time on:
        </Text>
        {WEEKDAYS.map((wd) => (
          <ToggleButton key={wd} size="small" shape="circular" checked={generalDays.has(wd)} onClick={() => toggleGeneral(wd)}>
            {WEEKDAY_SHORT[wd]}
          </ToggleButton>
        ))}
      </div>
      <WeekCalendar
        blocks={blocks}
        weekStart={week}
        editable
        showNav={false}
        defaultDurationMinutes={sessionLengthMinutes}
        selectedId={selected ? `pref-${selected.idx}` : null}
        onCreate={(b) => onChange([...value, { ...b, gymIds: [] }])}
        onMove={(id, b) => id.startsWith("pref-") && edit(Number(id.slice(5)), b)}
        onSelect={(id, anchor) => {
          if (!id.startsWith("pref-")) return;
          setTimeout(() => setSelected({ idx: Number(id.slice(5)), anchor }), 0);
        }}
      />
      <div className={s.legend}>
        <span>
          <span className={s.swatch} style={{ backgroundColor: tokens.colorBrandBackground2, border: `1px solid ${tokens.colorBrandStroke2}` }} />
          Trainer available
        </span>
        <span>
          <span className={s.swatch} style={{ backgroundColor: tokens.colorPaletteMarigoldBackground2, border: `1px dashed ${tokens.colorPaletteMarigoldBorder1}` }} />
          Your preferred times — drag on the grid to add, click to change gym or remove
        </span>
      </div>

      <Popover open={!!sel} positioning={{ target: selected?.anchor, position: "after", align: "top" }} onOpenChange={(_, d) => !d.open && setSelected(null)} trapFocus>
        <PopoverSurface style={{ width: 260, display: "flex", flexDirection: "column", gap: 8 }}>
          {sel && selected && (
            <>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Text weight="semibold" style={{ flex: 1 }}>
                  {WEEKDAY_LABEL[sel.weekday]} {sel.startTime}–{sel.endTime}
                </Text>
                <Button appearance="subtle" size="small" icon={<Dismiss16Regular />} aria-label="Close" onClick={() => setSelected(null)} />
              </div>
              {gyms.length > 1 ? (
                <>
                  <Text size={200}>Which gyms work for this slot? (none ticked = any)</Text>
                  {gyms.map((g) => (
                    <Checkbox key={g.id} label={g.name} checked={sel.gymIds.includes(g.id)} onChange={(_, d) => edit(selected.idx, { gymIds: d.checked ? [...sel.gymIds, g.id] : sel.gymIds.filter((x) => x !== g.id) })} />
                  ))}
                </>
              ) : (
                <Text size={200}>{gyms[0]?.name}</Text>
              )}
              <Button appearance="subtle" size="small" icon={<Delete16Regular />} onClick={() => remove(selected.idx)} style={{ alignSelf: "flex-start" }}>
                Remove
              </Button>
            </>
          )}
        </PopoverSurface>
      </Popover>
    </div>
  );
}
